import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const colorMap = {
  Python: "rgba(73, 144, 200, 0.5)",
  Typescript: "rgba(69, 150, 228, 0.5)",
  Javascript: "rgba(255, 235, 50, 0.5)",
  "AWS Lambda": "rgba(255, 180, 30, 0.5)",
  "AWS API Gateway": "rgba(255, 100, 100, 0.5)",
  Java: "rgba(103, 100, 255, 0.5)",
  JavaFX: "rgba(20, 135, 190, 0.5)",
  "React.js": "rgba(100, 150, 255, 0.5)",
  OpenAI: "rgba(159, 20, 159, 0.5)",
  Linux: "rgba(255, 218, 56, 0.5)",
  C: "rgba(188, 205, 224, 0.5)",
  HTML: "rgba(247, 96, 58, 0.5)",
  CSS: "rgba(41, 134, 202, 0.5)",
  Express: "rgba(210, 100, 80, 0.5)",
  "AWS S3": "rgba(106, 200, 69, 0.5)",
  MySQL: "rgba(88, 141, 201, 0.5)",
};

const ProjectCard = ({ project, index }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [imageIndex, setImageIndex] = useState(0);

  const techList = project.tech.split(", ");

  const toggleModal = () => setIsModalOpen(!isModalOpen);

  const changeSlide = (direction, length) => {
    setImageIndex((imageIndex + direction + length) % length);
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{
          opacity: { delay: index * 0.1, duration: 0.5 },
          y: { delay: index * 0.1, type: "spring", stiffness: 100 },
        }}
        viewport={{ once: true }}
        whileHover={{
          scale: 1.05,
          boxShadow: "0px 4px 15px rgba(0, 0, 0, 0.2)",
        }}
        whileTap={{ scale: 0.98 }}
        className="flex flex-col justify-between h-[350px] bg-[var(--projectsText)] overflow-hidden shadow-lg cursor-pointer m-4"
        onClick={toggleModal}
      >
        <div className="flex flex-col p-4 md:p-6 lg:p-8 opacity-100 text-white">
          <div className="font-bold text-lg md:text-xl lg:text-2xl">
            {project.name}
          </div>
          <div className="text-sm md:text-base">{project.date}</div>

          <div className="grid grid-cols-2 gap-x-2 md:gap-x-4 mt-2">
            {techList.map((tech) => (
              <div
                key={tech}
                className="flex items-center mr-2 mt-2 whitespace-nowrap"
              >
                <span
                  className="w-3 h-3 rounded-full mr-2"
                  style={{ backgroundColor: colorMap[tech] || "#323232" }}
                ></span>
                <span className="text-xs md:text-sm lg:text-base">{tech}</span>
              </div>
            ))}
          </div>
        </div>

        {project.link ? (
          <div className="flex justify-between items-center mx-4 my-4">
            <a
              href={project.link}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[var(--darkerGrey)] text-white p-2 pr-6 pl-6 md:pl-8 md:pr-8 font-bold text-xs md:text-sm lg:text-base"
            >
              Visit
            </a>
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleModal();
              }}
              className="bg-transparent text-white p-2 font-bold text-xs md:text-sm lg:text-base"
            >
              Read More
            </button>
          </div>
        ) : (
          <div className="flex justify-end items-center mx-4 my-4">
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleModal();
              }}
              className="bg-transparent text-white p-2 font-bold text-xs md:text-sm lg:text-base"
            >
              Read More
            </button>
          </div>
        )}
      </motion.div>

      <AnimatePresence>
        {isModalOpen && (
          <>
            <motion.div
              className="fixed inset-0 bg-black bg-opacity-50 z-40"
              onClick={toggleModal}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />

            <motion.div
              className="fixed inset-0 flex items-center justify-center z-50 p-4"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 50 }}
              transition={{ duration: 0.3 }}
            >
              <div className="bg-[var(--darkGrey)] text-white opacity-85 p-6 md:p-10 shadow-lg w-full md:w-5/6 lg:w-4/6 flex flex-col max-h-[90vh] overflow-auto">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
                  <h2 className="text-xl md:text-2xl lg:text-3xl font-bold mb-2">
                    {project.name}
                  </h2>
                  <div className="flex flex-col text-sm md:text-lg lg:text-xl font-bold">
                    <p>{project.date}</p>
                  </div>
                </div>
                <div className="flex flex-col lg:flex-row justify-between pt-5">
                  <div className="relative m-auto h-[300px] md:h-[400px] overflow-hidden w-full lg:w-2/3 mr-0 lg:mr-6">
                    <img
                      src={project.images[imageIndex]}
                      className="w-full h-full object-cover"
                    />
                    {project.images.length !== 1 && (
                      <>
                        <a
                          className="cursor-pointer absolute top-[50%] mt-[-32px] p-4 py-7 bg-[var(--darkGrey)] opacity-60 font-bold text-xs md:text-sm"
                          onClick={() => changeSlide(-1, project.images.length)}
                        >
                          &#10094;
                        </a>
                        <a
                          className="cursor-pointer absolute top-[50%] mt-[-32px] p-4 py-7 bg-[var(--darkGrey)] opacity-60 font-bold text-xs md:text-sm right-0"
                          onClick={() => changeSlide(1, project.images.length)}
                        >
                          &#10095;
                        </a>
                      </>
                    )}
                  </div>

                  <div className="flex flex-col justify-between w-full lg:w-1/3 mt-4 lg:mt-0">
                    <ul className="list-disc pl-5 space-y-2 mb-4 text-sm md:text-base lg:text-lg">
                      {project.desc.map((item, index) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                    <div className="grid grid-cols-2 gap-2">
                      {techList.map((tech, index) => (
                        <div
                          key={index}
                          className="px-3 py-2 text-center text-xs md:text-sm font-bold"
                          style={{
                            backgroundColor:
                              colorMap[tech] || "rgba(50, 50, 50, 0.5)",
                          }}
                        >
                          {tech}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <button
                  onClick={toggleModal}
                  className="bg-[var(--darkerGrey)] text-white p-2 mt-10 font-bold text-xs md:text-sm lg:text-base"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default ProjectCard;
